import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Channel, channelsService } from '../lib/api/channels';

import { BoardJournal } from './boardStore';

interface UserChannelsState {
    // State
    channels: Channel[];
    journals: BoardJournal[];
    isLoading: boolean;
    isLoaded: boolean; // True after first successful load
    lastFetchedAt: number | null;
    error: string | null;
    syncStatusMessages: Record<string, string>; // channelId -> status message

    // Actions
    loadChannels: (force?: boolean) => Promise<void>;
    setChannels: (channels: Channel[]) => void;
    addChannel: (channel: Channel) => void;
    updateChannel: (channelId: string, updates: Partial<Channel>) => void;
    setSyncStatusMessage: (channelId: string, message: string) => void;
    removeChannel: (channelId: string) => void;
    reset: () => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

export const useUserChannelsStore = create<UserChannelsState>()(
    devtools(
        (set, get) => ({
            // Initial state
            channels: [],
            journals: [],
            isLoading: false,
            isLoaded: false,
            lastFetchedAt: null,
            error: null,
            syncStatusMessages: {},

            setSyncStatusMessage: (channelId, message) => {
                set((state) => ({
                    syncStatusMessages: {
                        ...state.syncStatusMessages,
                        [String(channelId)]: message
                    }
                }));
            },

            // Load channels from API (with caching)
            loadChannels: async (force = false) => {
                const state = get();

                // Skip if already loading
                if (state.isLoading) {
                    return;
                }

                // Skip if already loaded and not expired (unless forced)
                if (!force && state.isLoaded && state.lastFetchedAt) {
                    const isExpired = Date.now() - state.lastFetchedAt > CACHE_DURATION_MS;
                    if (!isExpired) {
                        console.log('[UserChannelsStore] Using cached channels');
                        return;
                    }
                }

                set({ isLoading: true, error: null });

                try {
                    const response = await channelsService.getUserChannels();
                    if (response.success) {
                        // Merge REST data with current store state:
                        // Don't let a REST response downgrade a channel status from
                        // 'active' to 'syncing' — WebSocket events (channel:synced)
                        // are more authoritative for real-time status transitions.
                        const currentChannels = get().channels;
                        const mergedChannels = response.data.map((restCh: Channel) => {
                            const storeCh = currentChannels.find(c => String(c.id) === String(restCh.id));
                            if (storeCh && storeCh.status === 'active' && restCh.status === 'syncing') {
                                return { ...restCh, status: storeCh.status, lastSyncAt: storeCh.lastSyncAt || restCh.lastSyncAt };
                            }
                            return restCh;
                        });

                        set({
                            channels: mergedChannels,
                            journals: response.journals || [],
                            isLoading: false,
                            isLoaded: true,
                            lastFetchedAt: Date.now(),
                            error: null,
                        });
                        console.log('[UserChannelsStore] Loaded channels:', mergedChannels.length);
                    } else {
                        set({
                            isLoading: false,
                            error: 'Failed to load channels',
                        });
                    }
                } catch (error: any) {
                    console.error('[UserChannelsStore] Failed to load channels:', error);
                    set({
                        isLoading: false,
                        error: 'Unable to load channels. Please try again.',
                    });
                }
            },

            // Set channels directly (useful for WebSocket updates)
            setChannels: (channels) => {
                set({
                    channels,
                    isLoaded: true,
                    lastFetchedAt: Date.now(),
                    error: null,
                });
            },

            // Add a new channel
            addChannel: (channel) => {
                set((state) => ({
                    channels: [...state.channels, channel],
                }));
            },

            // Update a specific channel
            updateChannel: (channelId, updates) => {
                set((state) => ({
                    channels: state.channels.map((ch) =>
                        String(ch.id) === String(channelId) ? { ...ch, ...updates } : ch
                    ),
                }));
            },

            // Remove a channel
            removeChannel: (channelId) => {
                set((state) => ({
                    channels: state.channels.filter((ch) => ch.id !== channelId),
                }));
            },

            // Reset store (useful on logout)
            reset: () => {
                set({
                    channels: [],
                    isLoading: false,
                    isLoaded: false,
                    lastFetchedAt: null,
                    error: null,
                    syncStatusMessages: {},
                });
            },
        }),
        { name: 'user-channels-store' }
    )
);
